<template>
  <VanDialog
    v-model:show="store.showConditionModal"
    class="p-3 max-h-[80%] overflow-auto text-sm"
    closeOnClickOverlay
  >
    <div class="mb-4">
      <div class="flex justify-between items-center">
        <h2 class="text-lg">{{ t(`game.values.condition`) }}</h2>

        <div class="flex justify-center gap-2">
          <button class="btn btn-sm" @click="store.showConditionModal = false">
            {{ t(`story.g.cancel`) }}
          </button>
          <button class="btn btn-primary btn-sm" @click="clickConfirm">
            {{ t("Buttons.confirm") }}
          </button>
        </div>
      </div>

      <VanField
        v-model="conditionShowText"
        autosize
        class="mt-2"
        placeholder=""
        rows="2"
        type="textarea"
      />
    </div>

    <div class="flex gap-2">
      <h2 class="text-lg">{{ t(`game.values.operation`) }}</h2>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="op in Object.keys(textOpMap)"
          class="btn btn-primary btn-xs"
          @click="clickOperation(op as keyof typeof textOpMap)"
        >
          {{ t(`game.values.${op}`) }}
        </button>
      </div>
    </div>

    <!-- 角色数值管理 -->
    <template
      v-for="(item, idx) in [...store.form.characterzz, store.form.player]"
    >
      <div
        v-if="
          store.scoreList.filter((v) => v.characterId === item.id).length > 0
        "
        :key="item.id"
        class="flex gap-2.5 mt-2.5 bg-neutral p-2.5"
      >
        <!-- left -->
        <div class="w-[62px]">
          <BaseImg
            :media="store.characterIdMap[item.id!].avatarMedia"
            class="rounded-sm"
            ratio="3/4"
          />
          <p class="line-clamp-1 text-center mt-1">
            {{ store.characterIdMap[item.id!].name }}
          </p>
        </div>
        <!-- right -->
        <div
          :class="[
            'flex-1',
            store.scoreList.filter((v) => v.characterId === item.id).length ===
              0 && 'text-end',
          ]"
        >
          <ul>
            <template v-for="score in store.scoreList">
              <li v-if="score.characterId === item.id" :key="score.id">
                <div class="flex justify-between">
                  <div>
                    <p class="text-white">{{ score.name }}</p>
                    <p>
                      {{ $t(`game.values.init`) }}: {{ score.initialScore }}
                    </p>
                    <p>{{ score.description }}</p>
                  </div>
                  <div class="flex gap-1">
                    <button
                      class="btn btn-primary btn-xs"
                      @click="clickSelectValues(score)"
                    >
                      {{ t(`Buttons.select`) }}
                    </button>
                  </div>
                </div>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </template>

    <!-- 其他数值 -->
    <ul class="mt-2.5">
      <template v-for="score in store.scoreList">
        <li
          v-if="!score.characterId"
          :key="score.id"
          class="flex justify-between bg-my-dark p-2.5 mb-2.5"
        >
          <div>
            <p class="text-white">{{ score.name }}</p>
            <p>{{ $t(`game.values.init`) }}: {{ score.initialScore }}</p>
            <p>{{ score.description }}</p>
          </div>
          <div class="flex gap-1">
            <button
              class="btn btn-primary btn-xs"
              @click="clickSelectValues(score)"
            >
              {{ t(`Buttons.select`) }}
            </button>
          </div>
        </li>
      </template>
    </ul>

    <template #footer>
      <VanTextEllipsis
        :collapse-text="t('Buttons.collapse')"
        :content="t(`game.values.tip`)"
        :expand-text="t('Buttons.expand')"
      />
    </template>
  </VanDialog>
</template>

<script lang="ts" setup>
import i18n from "@/locales";
import { useGalGameEditor } from "@/stores/galGameEditor";
import { showFailToast } from "vant";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const store = useGalGameEditor();
const { t } = useI18n();

watch(
  () => store.showConditionModal,
  (show) => {
    if (show) {
      const { score } = store.activeNode;
      if (score) {
        conditionText.value = score.replace(/_(\d+)_(\d+)/g, "{$1}.{$2}");
      } else {
        conditionText.value = "";
      }
    }
  }
);

const characterIdNameMap = computed(() => {
  const map: Record<number, string> = {};
  for (const id in store.characterIdMap)
    map[id] = store.characterIdMap[id].name!;

  return map;
});

const characterNameIdMap = computed(() => {
  const map: Record<string, number> = {};
  for (const id in store.characterIdMap)
    map[store.characterIdMap[id].name!] = Number(id);
  return map;
});

const valueIdNameMap = computed(() => {
  const map: Record<number, string> = {};
  for (const score of store.scoreList) map[score.id!] = score.name;
  return map;
});

const valueNameIdMap = computed(() => {
  const map: Record<string, GalGameScore> = {};
  for (const score of store.scoreList) map[score.name] = score;
  return map;
});

const conditionText = ref("");
const conditionShowText = computed({
  get: () => {
    let text = conditionText.value;
    if (text === "") return "";
    // 替换操作符
    Object.entries(opTextMap).forEach(([op, textOp]) => {
      text = text.replace(new RegExp(op, "g"), textOp);
    });

    // 替换名称
    return text.replace(
      /\{(\w+)\}\.\{(\w+)\}/g,
      (match, characterId, valueId) => {
        // 获取 characterName 和 valueName
        const valueName = valueIdNameMap.value[Number(valueId)];
        if (characterId === "0") return `{${valueName}}`;
        const characterName = store.characterIdMap[characterId].name;
        // 如果找到了名称，则替换；否则保持原样
        return `{${characterName}}.{${valueName}}`;
      }
    );
  },
  set(value) {
    conditionText.value = replaceNamesWithIds(value);
  },
});

function replaceNamesWithIds(expression: string) {
  // 对于每个可能的名称进行替换，这需要逐一替换以避免混淆
  Object.entries(characterNameIdMap.value).forEach(([name, id]) => {
    expression = expression.replace(
      new RegExp(`\\{${name}\\}`, "g"),
      `{${id}}`
    );
  });
  Object.entries(valueNameIdMap.value).forEach(([name, score]) => {
    const { id, characterId } = score;
    let target = characterId ? `{${id}}` : `{0}.{${id}}`;
    expression = expression.replace(new RegExp(`\\{${name}\\}`, "g"), target);
  });

  // 替换操作符
  Object.entries(textOpMap).forEach(([textOp, op]) => {
    expression = expression.replace(new RegExp(textOp, "g"), op);
  });

  return expression;
}

const textOpMap = {
  and: "&&",
  or: "||",
  greater: ">",
  less: "<",
  greaterEqual: ">=",
  lessEqual: "<=",
};

const opTextMap = {
  "\\&\\&": "and",
  "\\|\\|": "or",
  // "\\>": "greater",
  // "\\<": "less",
  // "\\>\\=": "greaterEqual",
  // "\\<\\=": "lessEqual",
};

const clickOperation = (op: keyof typeof textOpMap) => {
  conditionText.value += ["and", "or"].includes(op)
    ? ` ${op} `
    : `${textOpMap[op]}`;
};

function clickConfirm() {
  let score = conditionText.value;
  const isValidFormat =
    /^(\{\w+\}\.\{\w+\}(?:[><]=?|==)\d+(?:\s*(?:\&\&|\|\|)\s*\{\w+\}\.\{\w+\}(?:[><]=?|==)\d+)*)$/g.test(
      score
    );

  if (!isValidFormat) {
    return showFailToast(t("game.values.conditionFormatError"));
  }

  score = score.replace(/\{(\d+)\}\.\{(\d+)\}/g, "_$1_$2");

  // 替换逻辑运算符
  score = score
    .replace(/\band\b/g, "&&")
    .replace(/\bor\b/g, "||")
    .replace(/ /g, "");

  store.activeNode.score = score;
  store.saveNode();
  store.showConditionModal = false;
}

const clickSelectValues = (score: GalGameScore) => {
  let { characterId, id } = score;
  if (!characterId) characterId = 0;
  conditionText.value += `{${characterId}}.{${id}}`;
};
</script>
